import React, { useRef } from "react";
import styled from "styled-components";

/**
 * Barra de abas padrao do sistema: texto laranja + sublinhado laranja na ativa,
 * contador opcional em pilula. E o mesmo visual de Agendamentos e CRM Pet,
 * extraido para um lugar so para que as demais telas nao reinventem o estilo.
 *
 * Uso:
 *   <PageTabs
 *     tabs={[{ value: "dados", label: "Dados", count: 3 }]}
 *     value={abaAtiva}
 *     onChange={setAbaAtiva}
 *   />
 */

const Row = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  overflow-x: auto;
  border-bottom: 2px solid ${(p) => p.theme.colors.gray[200]};

  /* a barra de rolagem so aparece quando faz falta, para a linha nao engrossar */
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 4px;
  }
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: max-content;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? "bold" : "normal")};
  color: ${(p) =>
    p.$active ? p.theme.colors.scale[700] : p.theme.colors.gray[500]};
  background: none;
  border: none;
  border-bottom: 3px solid
    ${(p) => (p.$active ? p.theme.colors.scale[500] : "transparent")};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;

  &:hover:not(:disabled) {
    color: ${(p) => p.theme.colors.scale[600]};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => p.theme.colors.scale[300]};
    border-radius: 4px 4px 0 0;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Count = styled.span`
  background: ${(p) =>
    p.$active ? p.theme.colors.scale[500] : p.theme.colors.gray[200]};
  color: ${(p) => (p.$active ? "#fff" : p.theme.colors.gray[600])};
  border-radius: 20px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: bold;
`;

export function PageTabs({ tabs = [], value, onChange, ariaLabel, ...rest }) {
  const refs = useRef({});

  const selecionavel = tabs.filter((tab) => !tab.disabled);

  const irPara = (proxima) => {
    if (!proxima) return;
    refs.current[proxima.value]?.focus();
    onChange?.(proxima.value);
  };

  const handleKeyDown = (event) => {
    const atual = selecionavel.findIndex((tab) => tab.value === value);
    if (atual < 0) return;

    const total = selecionavel.length;
    let proximo = null;
    if (event.key === "ArrowRight") proximo = (atual + 1) % total;
    if (event.key === "ArrowLeft") proximo = (atual - 1 + total) % total;
    if (event.key === "Home") proximo = 0;
    if (event.key === "End") proximo = total - 1;
    if (proximo === null) return;

    event.preventDefault();
    irPara(selecionavel[proximo]);
  };

  return (
    <Row role="tablist" aria-label={ariaLabel} {...rest}>
      {tabs.map((tab) => {
        const ativa = tab.value === value;

        return (
          <TabButton
            key={tab.value}
            ref={(el) => {
              refs.current[tab.value] = el;
            }}
            type="button"
            role="tab"
            aria-selected={ativa}
            tabIndex={ativa ? 0 : -1}
            disabled={tab.disabled}
            title={tab.title || tab.label}
            $active={ativa}
            onClick={() => !tab.disabled && onChange?.(tab.value)}
            onKeyDown={handleKeyDown}
          >
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <Count $active={ativa}>{tab.count}</Count>
            )}
          </TabButton>
        );
      })}
    </Row>
  );
}

export default PageTabs;
